import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Card, CardContent, Link } from '@mui/material';
import axiosInstance from 'utils/axiosUtil';
import Chart from 'react-apexcharts';
import { withStyles } from '@mui/styles';
import { EncounterTypeUtil, StoreUtil } from 'doctivity-shared/utils';

const styles = (theme) => ({
  container: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    height: 544,
    display: 'flex',
    flexWrap: 'nowrap',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    height: '48px',
    maxHeight: '48px',
    minHeight: '48px',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  headerTitle: {
    fontSize: '20px',
    display: 'flex',
    flexWrap: 'nowrap',
    textWrap: 'nowrap',
  },
  cardBody: {},
  apexIcons: {
    fontFamily: 'Material Icons !important',
    fontSize: '16px',
    marginTop: '2px',
  },
  allActivityLink: {
    cursor: 'pointer',
    color: '#214E56',
    fontSize: '12px',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: '24px',
  },
});
// Bar colors keyed by encounter type id. Add a color here when a new
// encounter type is added to EncounterTypeUtil.
const TYPE_COLORS = {
  1: '#FFBF69',
  2: '#FA9090',
  3: '#F219D0',
  4: '#2680E9',
  5: '#04CBE8',
  6: '#02E39D',
  7: '#9937BA',
  8: '#5C6BC0',
  9: '#A1887F',
};
const UNSPECIFIED_COLOR = '#C6C6C6';
const FALLBACK_COLOR = '#8C8C8C';

// Shorter y-axis labels for the longer encounter type names.
const SHORT_LABELS = {
  4: 'Provider',
  6: 'Virtual',
};

// Encounter types in id order, with notes that have no type last.
const UNSPECIFIED_KEY = 'unspecified';
const SortedEncounters = [
  ...[...EncounterTypeUtil.getAll()].sort((a, b) => a.id - b.id),
  { id: null, name: 'Unspecified', icon: 'help_outline' },
];
const encounterKey = (id) => (id == null ? UNSPECIFIED_KEY : id);

const colors = SortedEncounters.map((t) =>
  t.id == null ? UNSPECIFIED_COLOR : TYPE_COLORS[t.id] || FALLBACK_COLOR,
);
const categories = SortedEncounters.map((t) => [SHORT_LABELS[t.id] || t.name]);

// The x-axis always shows at least 0 - 4 so that a user with few (or no)
// activity notes still gets whole-number ticks (0, 1, 2, 3, 4) instead of
// fractional ones that round to duplicates (0, 1, 1, 2, 2).
const MIN_AXIS_MAX = 4;
const MAX_TICKS = 6;
// Step multipliers that keep every tick on a whole number.
const STEP_MULTIPLIERS = [1, 2, 2.5, 5];

// Given the largest value in the series, return an axis max and tick count
// where every tick lands on a whole number.
function calculateAxisScale(maxValue) {
  const safeMax = Math.max(
    MIN_AXIS_MAX,
    Number.isFinite(maxValue) ? Math.ceil(maxValue) : 0,
  );
  // Smallest "nice" step (1, 2, 5, 10, 20, 25, 50, ...) that keeps the
  // number of ticks reasonable.
  let step = 1;
  for (let power = 0; power < 12; power += 1) {
    const magnitude = 10 ** power;
    const candidate = STEP_MULTIPLIERS.map(
      (multiplier) => multiplier * magnitude,
    )
      .filter(Number.isInteger)
      .find((value) => Math.ceil(safeMax / value) <= MAX_TICKS);
    if (candidate) {
      step = candidate;
      break;
    }
  }
  const tickAmount = Math.max(1, Math.ceil(safeMax / step));

  return { min: 0, max: step * tickAmount, tickAmount };
}

const DEFAULT_AXIS_SCALE = calculateAxisScale(0);

function ActivityNotesSummaryChart({ classes }) {
  const clientId = useSelector(({ app }) => app.selectedClient);
  const userId = useSelector(({ user }) => user.id);
  const editComment = useSelector(({ providerComments }) =>
    StoreUtil.get(providerComments, StoreUtil.COMMON_EDIT_ITEM),
  );
  const [loadedFor, setLoadedFor] = useState({
    clientId: undefined,
    userId: undefined,
  });
  const addIcons = useCallback(
    (/*chartContext, config*/) => {
      const elems = document.querySelectorAll('.apexcharts-yaxis-label');
      elems.forEach((elem, index) => {
        elem.style.fill = colors[index];
        const text = elem.getElementsByTagName('tspan');
        if (text && text.length === 1) {
          const child = text[0].cloneNode(true);
          text[0].setAttribute('dx', '-16px');
          text[0].setAttribute('dy', '2px');
          // Safari uses the width of the icon text name to figure out the width
          // of the icon but doesn't seem to want to respect any other options
          child.setAttribute('x', '-22px');
          child.setAttribute('dy', '4px');
          child.setAttribute('text-anchor', 'start');
          child.setAttribute(
            'class',
            `material-symbols-outlined ${classes.apexIcons}`,
          );
          child.innerHTML = SortedEncounters[index].icon;
          elem.append(child);
        }
      });
    },
    [],
  );
  const [chartOptions, setChartOptions] = useState({
    series: [{ data: SortedEncounters.map(() => 0) }],
    options: {
      colors,
      chart: {
        id: 'activity-chart',
        toolbar: {
          show: false,
        },
        type: 'bar',
        events: {
          mounted: addIcons,
          updated: addIcons,
        },
      },
      grid: {
        padding: {
          left: 32,
          bottom: 8,
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
        xaxis: {
          lines: {
            show: true,
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '35%',
          borderRadius: 5,
          borderRadiusApplication: 'end',
          distributed: true,
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        offsetX: -10,
        background: {
          enabled: true,
          borderRadius: 5,
          opacity: 0.1,
          borderWidth: 1,
        },
        total: { enabled: true },
      },
      legend: {
        show: false,
      },
      yaxis: {
        style: {
          fontSize: '14px',
          colors: ['#dcdcdc'],
        },
        title: {
          style: {
            color: '#dcdcdc',
          },
        },
        labels: {
          maxWidth: 400,
          minWidth: 80,
          offsetY: 6,
        },
        stepSize: 2,
      },
      xaxis: {
        categories,
        decimalsInFloat: 0,
        min: DEFAULT_AXIS_SCALE.min,
        max: DEFAULT_AXIS_SCALE.max,
        tickAmount: DEFAULT_AXIS_SCALE.tickAmount,
        axisTicks: {
          color: '#C6C6C6',
        },
        labels: {
          style: {
            fontSize: '12px',
            colors: ['#000'],
          },
          showDuplications: false,
          formatter: function (val) {
            return `${Math.round(val)}`;
          },
        },
        title: {
          style: {
            color: '#C6C6C6',
          },
        },
      },
    },
  });
  const recentActivityNotes = useSelector(({ dashboard }) =>
    StoreUtil.get(dashboard, `analytics-recent-activitynotes-${clientId}`),
  );
  const loadCounts = useCallback(async () => {
    const results = await axiosInstance.get(
      `/v2/my/comments/monthly_activity?clientId=${encodeURIComponent(clientId)}`,
    );
    if (results && results.data) {
      const counts = results.data.reduce((acc, item) => {
        acc[encounterKey(item.encounter_type_id)] = item.total;
        return acc;
      }, {});
      const series = SortedEncounters.map(
        (type) => Number(counts[encounterKey(type.id)] ?? 0),
      );
      const maxCount = Math.max(0, ...series);
      const { min, max, tickAmount } = calculateAxisScale(maxCount);

      setChartOptions((previous) => ({
        ...previous,
        series: [{ name: 'Total', data: series }],
        options: {
          ...previous.options,
          xaxis: {
            ...previous.options.xaxis,
            min,
            max,
            tickAmount,
          },
        },
      }));
    }
  }, [clientId, userId]);

  useEffect(() => {
    if (StoreUtil.isSaved(editComment)) {
      loadCounts();
    }
  }, [editComment]);

  useEffect(() => {
    if (clientId && userId && loadedFor.clientId !== clientId) {
      setLoadedFor({ clientId, userId });
      loadCounts();
    }
  }, [recentActivityNotes, clientId, userId]);

  return (
    <Card className={classes.container}>
      <CardContent className={classes.cardContent}>
        <div className={classes.header}>
          <div className={classes.headerTitle}>
            <span>Your Activity This Month</span>
          </div>
        </div>
        <div className={classes.cardBody}>
          <Chart
            style={{ border: '1px solid #dcdcdc' }}
            options={chartOptions.options}
            series={chartOptions.series}
            type='bar'
            height={420}
          />
        </div>
      </CardContent>
      <Link className={classes.allActivityLink} href={'/notes_report'}>
        See activity note reports
      </Link>
    </Card>
  );
}

ActivityNotesSummaryChart.propTypes = {
  classes: PropTypes.object.isRequired,
};

const styled = withStyles(styles)(ActivityNotesSummaryChart);
export { styled as ActivityNotesSummaryChart };
